/**
 * Reading a thrown value.
 *
 * `catch` hands the app a value of *unknown* shape — the language says nothing
 * about it, and the hub, the socket and `fetch` all throw different things
 * (`Error`, an `ApiError` with `.status`/`.data`, an axios-shaped error with
 * `.response.data`, a bare string from a rejected IPC call). Before this file,
 * every catch wrote `catch (err: any)` and read `err.message`/`err.response`
 * straight off it — 599 `any`s that were the single largest class in the
 * `no-explicit-any` backlog (card t_016c7a0e).
 *
 * The narrowing lives here, once, so call sites stay `catch (err)` and read the
 * message through `errorMessage(err, '…')` / `hubErrorMessage(err, '…')`.
 */

/** The shape of everything this app throws, all fields optional. */
export interface ErrorLike {
  name?: string;
  message?: string;
  /** Set by `createApiError` (services/api.ts) and by Node/http errors. */
  status?: number;
  code?: string | null;
  /** The parsed error body `apiFetch` attaches. */
  data?: unknown;
  /** axios-shaped callers (`error.response.data.code`). */
  response?: { status?: number; data?: unknown } | null;
}

/** A hub error body — `{ error }` / `{ message }` / `{ code }`. */
interface ErrorBody {
  error?: string;
  message?: string;
  code?: string | null;
}

/** The value as an `ErrorLike` record; `{}` for anything that is not one. */
export function asErrorLike(value: unknown): ErrorLike {
  return value && typeof value === 'object' ? (value as ErrorLike) : {};
}

/** True when the thrown value carries a message this app can show. */
export function isErrorLike(value: unknown): value is ErrorLike {
  return value instanceof Error || Boolean(asErrorLike(value).message);
}

/**
 * The message a thrown value carries, or `fallback`.
 *
 * Replaces the `err.message || 'Failed to …'` idiom: an `Error`, a string throw
 * and a plain `{ message }` object all answer, and an empty message falls back
 * exactly as the `||` did.
 */
export function errorMessage(value: unknown, fallback = 'Unknown error'): string {
  if (typeof value === 'string') return value.length > 0 ? value : fallback;
  if (value instanceof Error) return value.message || fallback;
  const message = asErrorLike(value).message;
  return typeof message === 'string' && message.length > 0 ? message : fallback;
}

/** The HTTP status a thrown value carries, when it carries one. */
export function errorStatus(value: unknown): number | undefined {
  const candidate = asErrorLike(value);
  const status = candidate.status ?? candidate.response?.status;
  return typeof status === 'number' ? status : undefined;
}

/** The `name` a thrown value carries (`NotAllowedError`, `AbortError`, …). */
export function errorName(value: unknown): string | undefined {
  const name = asErrorLike(value).name;
  return typeof name === 'string' && name.length > 0 ? name : undefined;
}

/** The machine-readable code a thrown value carries, when it carries one. */
export function errorCode(value: unknown): string | null {
  const candidate = asErrorLike(value);
  const body = asErrorLike(candidate.data);
  const code = candidate.code ?? body.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/**
 * The message inside an error *body* — the hub's `{ error }` /`{ message }`,
 * which `apiFetch` attaches to the thrown `ApiError` as `data` and which an
 * axios-shaped caller carries at `response.data`. Falls back to the thrown
 * value's own message, then to `fallback`.
 *
 * Replaces the `err.response?.data?.error || 'Failed to …'` idiom.
 */
export function hubErrorMessage(value: unknown, fallback = 'Request failed'): string {
  const candidate = asErrorLike(value);
  const bodies: Array<ErrorBody> = [(candidate.data ?? {}) as ErrorBody, (candidate.response?.data ?? {}) as ErrorBody];
  for (const body of bodies) {
    const text = body.error ?? body.message;
    if (typeof text === 'string' && text.length > 0) return text;
  }
  return errorMessage(value, fallback);
}

/** A `console.warn`/`console.error` prefix that says where the value came from. */
export function describeError(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  return errorMessage(value, 'unknown error');
}
